import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Dimensions, ScrollView, View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { cloneDeep, isEqual } from 'lodash';

import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import tefap from '../../utils/tefap';
import Selector from '../form/Selector';
import NavBar from '../NavBar';

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    flex: 1,
    alignItems: 'center',
  },
  mainContent: {
    width: '100%',
    maxWidth: 720,
    flex: 1,
  },
  infoCard: {
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    backgroundColor: Colors.white,
    marginBottom: 40,
  },
  contentContainer: {
    color: Colors.primaryColor,
    backgroundColor: Colors.white,
    paddingBottom: 160,
    position: 'relative',
  },
  benefitsCard: {
    padding: 16,
    borderBottomColor: Colors.white,
    borderBottomWidth: 0,
  },
  subheader: {
    paddingBottom: 22,
    paddingLeft: Layout.isCompactScreen ? 16 : 0,
  },
  subheaderText: {
    fontSize: 16,
    color: Colors.mediumBlue,
    fontWeight: 'bold',
  },
  programDescription: {
    marginTop: 40,
    marginLeft: 20,
  },
  boldText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  selector: {
    marginTop: 24,
    width: '100%',
  },
  label: {
    color: Colors.grayText,
    fontSize: 12,
  },
  dropdownIcon: {
    color: Colors.lightGray,
  },
  dropdownPlaceholder: {
    color: Colors.grayText,
    fontFamily: 'roboto-regular',
  },
  dropdown: {
    width: Dimensions.get('window').width - 32,
    maxWidth: 688,
    borderColor: Colors.white,
    backgroundColor: Colors.white,
  },
  cardView: {
    marginHorizontal: 0,
    marginVertical: 0.5,
    backgroundColor: Colors.lightBackground,
    paddingVertical: 20,
    elevation: 2,
  },
  descriptionText: {
    paddingTop: 36,
    paddingBottom: 16,
    paddingLeft: 8,
    fontSize: 20,
    color: '#808080',
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 20,
    color: Colors.mediumBlue,
    fontWeight: 'bold',
  },
  textStyle: {
    fontSize: 20,
    color: Colors.veryDarkGray,
  },
  verifyText: {
    fontSize: 18,
    color: Colors.profileVerified,
  },
  desSec: {
    padding: 10,
  },
  viewWithRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  viewWithXtaPadding: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 36,
  },
  dashedUnderline: {
    borderWidth: 1,
    borderColor: Colors.grayBorder,
    borderStyle: 'dashed',
    borderRadius: 1,
    width: '80%',
    marginBottom: 16,
  },
  detailText: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.veryDarkGray,
    maxWidth: Layout.fullWidth - 100,
  },
  activeText: {
    color: Colors.veryDarkGray,
  },
  labelTextStyle: {
    position: 'absolute',
    left: '100%',
  },
  halfWidth: {
    width: '50%',
  },
  saveChangesContainer: {
    position: 'absolute',
    backgroundColor: Colors.mediumBlue,
    height: 56,
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelSaveChanges: {
    paddingLeft: 16,
  },
  saveChangesText: {
    color: Colors.white,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginRight: 16,
  },
  deleteAccountContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  deleteAccountText: {
    color: Colors.brightRed,
    fontSize: 16,
  },
});

class UserBenefits extends Component {
  static propTypes = {
    translation: PropTypes.objectOf(PropTypes.any).isRequired,
    actions: PropTypes.objectOf(PropTypes.any).isRequired,
    profile: PropTypes.objectOf(PropTypes.any).isRequired,
    navigation: PropTypes.objectOf(PropTypes.any).isRequired,
    registration: PropTypes.objectOf(PropTypes.any).isRequired,
  };

  constructor(props) {
    super();
    this.state = {
      errors: {},
      user: cloneDeep(props.profile),
      showChangeLanguage: false,
      showSaveChanges: false,
      pushNotificationRegistration: false,
      switchesLoaded: false,
    };
  }

  componentDidMount = () => {
    setTimeout(() => {
      this.setState({ switchesLoaded: true, showSaveChanges: false });
    }, 500);
  };

  componentDidUpdate = () => {
    const { user } = this.state;
    const { registration } = this.props;
    const { user: newUser } = registration;
    if (user.id !== newUser.id) {
      this.setState({ user: deepClone(newUser) });
    }
  };

  somethingChanged = (user) => {
    const { profile } = this.props;
    const updates = {};
    Object.keys(profile).forEach((key) => {
      if (!isEqual(profile[key], user[key])) updates[key] = user[key];
    });
    return { changes: updates, changed: !!Object.keys(updates).length };
  };

  setSelectorChanges = (program_id, value) => {
    const { user } = this.state;
    const program = user.assistance_programs.find(
      (program) => program.public_assistance_program_id === program_id
    );
    const participates = value && Number(value);

    if (program !== undefined) {
      program.participates = participates;
    } else {
      user.assistance_programs.push({
        public_assistance_program_id: program_id,
        participates: participates,
      });
    }

    const updates = this.somethingChanged(user);
    this.setState({ user, showSaveChanges: updates.changed });
  };

  cancelShowSaveChanges = () => {
    this.setState({ showSaveChanges: false });
  };

  confirmSaveChanges = () => {
    const { user } = this.state;
    const { actions } = this.props;
    this.setState({ showSaveChanges: false });
    const updates = this.somethingChanged(user);
    if (updates.changed) {
      actions.updateUser(updates.changes);
    }
  };

  capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  render() {
    const { translation, profile, registration, actions } = this.props;
    const { showSaveChanges, switchesLoaded, user } = this.state;

    const labels = {
      benefits: this.capitalize(translation.benefits),
      save_changes: translation.fragment_profile_button_savechanges,
    };
    const programs = tefap.getStatePrograms(registration.tefapConfig, user);

    return (
      <View style={styles.container}>
        <NavBar title={labels.benefits} onPressBack={() => this.props.navigation.navigate('landing')} />

        {showSaveChanges && switchesLoaded ? (
          <View style={styles.saveChangesContainer}>
            <TouchableOpacity onPress={this.cancelShowSaveChanges} style={styles.cancelSaveChanges}>
              <MaterialIcons name={'close'} size={24} color={Colors.white} />
            </TouchableOpacity>
            <TouchableOpacity onPress={this.confirmSaveChanges} style={styles.cancelSaveChanges}>
              <Text style={styles.saveChangesText}>{labels.save_changes}</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <View style={styles.mainContent}>
          <ScrollView style={styles.infoCard} contentContainerStyle={styles.contentContainer}>
            <View style={styles.programDescription}>
              <Text style={styles.boldText}>{translation.tefap_benefit_programs_select}</Text>
            </View>
            <View style={styles.benefitsCard}>
              <View stackedLabel picker style={styles.row}>
                {programs.map((program, index) => (
                  <View key={index} style={styles.selector}>
                    <Selector
                      key={index}
                      field={program.id}
                      label={program.name}
                      onChange={this.setSelectorChanges}
                      currentValue={tefap.userParticipates(user, program.id)}
                      translation={translation}
                      extraOptions={[
                        { name: 'Yes', value: 1 },
                        { name: 'No', value: 0 },
                      ]}
                    />
                  </View>
                ))}
              </View>
            </View>
          </ScrollView>
        </View>
      </View>
    );
  }
}

export default UserBenefits;
